import {useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {createPersister} from '@github-ui/react-query-persister'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {Type} from '@github-ui/react-query-persister/typebox'
import {useAnalytics} from '@github-ui/use-analytics'
import {fetchCAPI} from '../utils/fetch-capi'

/** Wire shape returned by MC's GET /agents/devices. */
type CapiDeviceDirectory = {
  path: string
  session_count: number
}
type CapiDevice = {
  device_id: string
  display_name?: string
  directories: CapiDeviceDirectory[]
}

/** A single working-directory bucket on a device. */
export type ConnectedDeviceDirectory = {
  path: string
  sessionCount: number
}

/** Normalised device for UI consumption. */
export type ConnectedDevice = {
  id: string
  displayName: string
  /** Active steerable directories on this device. Always non-empty when
   * the device appears in the list; sorted server-side by path. */
  directories: ConnectedDeviceDirectory[]
}

const ConnectedDeviceSchema = Type.Object({
  id: Type.String(),
  displayName: Type.String(),
  directories: Type.Array(
    Type.Object({
      path: Type.String(),
      sessionCount: Type.Number(),
    }),
  ),
})

const ConnectedDevicesValidator = Compile(Type.Array(ConnectedDeviceSchema))

const CONNECTED_DEVICES_PATH = '/agents/devices'
const POLL_INTERVAL_MS = 30_000

/**
 * Fetches the list of remote devices the current user has connected. Hits the
 * Mission Control endpoint directly through CAPI and persists the most recent
 * response in IndexedDB so the device list renders instantly on subsequent loads.
 */
export function useConnectedDevices() {
  const enabled = isFeatureEnabled('copilot_mission_control_remote_kickstart')
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createPersister({
        validator: ConnectedDevicesValidator,
        sendAnalyticsEvent,
        prefix: 'copilot-remote-connected-devices',
        enabled,
      }),
    [sendAnalyticsEvent, enabled],
  )

  return useQuery<ConnectedDevice[]>({
    queryKey: ['copilot-remote-connected-devices'],
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    queryFn: async () => {
      try {
        const response = await fetchCAPI({path: CONNECTED_DEVICES_PATH})
        const data = (await response.json()) as {devices?: CapiDevice[]}
        return (
          (data.devices ?? [])
            .map(d => ({
              id: d.device_id,
              displayName: d.display_name || d.device_id,
              directories: (d.directories ?? []).map(dir => ({
                path: dir.path,
                sessionCount: dir.session_count,
              })),
            }))
            // Devices with no active directories aren't actionable — the user
            // can't dispatch a start_session anywhere on them.
            .filter(d => d.directories.length > 0)
        )
      } catch {
        return []
      }
    },
    persister,
  })
}
